import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonModal,
  IonSpinner,
} from '@ionic/react';

import {
  AndroidDebugScenario,
  CheckAgeSignalsResult,
  CheckEligibilityResult,
  CheckRequiredRegulatoryFeaturesResult,
  InsightAgeCheck,
  RequestSignificantUpdateApprovalResult,
  RuntimeEnvironmentResult,
  ShowSignificantUpdateAcknowledgmentResult,
} from 'insight-age-check';

import './AgeCheckTestModal.css';
import {
  AgeGateStartupDebugMode,
  ageGateStartupDebugOptions,
  getStoredAgeGateStartupDebugMode,
  setStoredAgeGateStartupDebugMode,
} from '../ts/ageGateDebug';
import {
  clearStoredIosAdultAcknowledgment,
  getIosSignificantUpdateDescription,
  hasStoredIosAdultAcknowledgment,
  setStoredIosAdultAcknowledgment,
} from '../ts/iosAgeAssurance';

interface AgeCheckTestModalProps {
  appName: string;
  isOpen: boolean;
  onClose: () => void;
  platform: string;
}

const defaultAgeGates = '13, 15, 18';

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error.';
};

const parseAgeGates = (ageGateInput: string) => {
  const parsedAgeGates = ageGateInput
    .split(',')
    .map(value => Number(value.trim()))
    .filter(value => !Number.isNaN(value));

  if (parsedAgeGates.length < 2 || parsedAgeGates.length > 3) {
    throw new Error('Use two or three comma-separated age gates.');
  }

  return parsedAgeGates;
};

function AgeCheckTestModal({ appName, isOpen, onClose, platform }: AgeCheckTestModalProps) {
  const [ageGateInput, setAgeGateInput] = useState(defaultAgeGates);
  const [updateDescription, setUpdateDescription] = useState(getIosSignificantUpdateDescription(appName));
  const [androidDebugScenario, setAndroidDebugScenario] = useState(AndroidDebugScenario.Live);
  const [runtimeEnvironmentResult, setRuntimeEnvironmentResult] = useState<RuntimeEnvironmentResult | null>(null);
  const [startupDebugMode, setStartupDebugMode] = useState(AgeGateStartupDebugMode.Auto);
  const [storedAdultAcknowledgment, setStoredAdultAcknowledgment] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState<CheckEligibilityResult | null>(null);
  const [regulatoryFeaturesResult, setRegulatoryFeaturesResult] = useState<CheckRequiredRegulatoryFeaturesResult | null>(null);
  const [ageSignalsResult, setAgeSignalsResult] = useState<CheckAgeSignalsResult | null>(null);
  const [significantUpdateAcknowledgmentResult, setSignificantUpdateAcknowledgmentResult] = useState<ShowSignificantUpdateAcknowledgmentResult | null>(null);
  const [significantUpdateResult, setSignificantUpdateResult] = useState<RequestSignificantUpdateApprovalResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeAction, setActiveAction] = useState('');

  const isSupportedPlatform = platform === 'ios' || platform === 'android';
  const isAndroid = platform === 'android';
  const isIos = platform === 'ios';
  const canCheckEligibility = platform === 'ios';
  const canCheckRequiredRegulatoryFeatures = platform === 'ios';
  const canShowSignificantUpdateAcknowledgment = platform === 'ios';
  const canRequestSignificantUpdateApproval = platform === 'ios';
  const isBusy = activeAction.length > 0;
  const supportsStartupOverride = isSupportedPlatform && Boolean(runtimeEnvironmentResult?.isDebugBuild);
  const title = isAndroid ? 'Google Play Age Signals Test' : 'Apple Age Assurance Test';
  const intro = isAndroid
    ? 'Use this screen to exercise the native Android plugin before we wire the full launch flow.'
    : 'Use this screen to exercise the full iOS 26.4 Apple age assurance and significant update flows on a real device before broader rollout.';

  useEffect(() => {
    setUpdateDescription(getIosSignificantUpdateDescription(appName));
  }, [appName]);

  useEffect(() => {
    if (!isOpen || !isSupportedPlatform) {
      return;
    }

    let cancelled = false;

    const loadDebugContext = async () => {
      try {
        const [runtimeEnvironment, storedStartupMode, adultAcknowledgmentStored] = await Promise.all([
          InsightAgeCheck.getRuntimeEnvironment(),
          getStoredAgeGateStartupDebugMode(),
          isIos ? hasStoredIosAdultAcknowledgment(appName) : Promise.resolve(false),
        ]);

        if (cancelled) {
          return;
        }

        console.log('[AgeCheckTest] runtime environment result', runtimeEnvironment);
        console.log('[AgeCheckTest] stored startup debug mode', storedStartupMode);
        console.log('[AgeCheckTest] stored adult acknowledgment', adultAcknowledgmentStored);
        setRuntimeEnvironmentResult(runtimeEnvironment);
        setStartupDebugMode(storedStartupMode);
        setStoredAdultAcknowledgment(adultAcknowledgmentStored);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setErrorMessage(getErrorMessage(error));
      }
    };

    loadDebugContext().catch(() => {
      /** Error state is already surfaced above. */
    });

    return () => {
      cancelled = true;
    };
  }, [appName, isIos, isOpen, isSupportedPlatform]);

  const runAction = async (actionName: string, action: () => Promise<void>) => {
    setActiveAction(actionName);
    setErrorMessage('');

    try {
      await action();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setActiveAction('');
    }
  };

  const handleEligibilityCheck = async () => {
    await runAction('Checking eligibility', async () => {
      console.log('[AgeCheckTest] running iOS eligibility check');
      const result = await InsightAgeCheck.checkEligibility();
      console.log('[AgeCheckTest] iOS eligibility result', result);
      setEligibilityResult(result);
    });
  };

  const handleSaveStartupOverride = async () => {
    await runAction('Saving startup override', async () => {
      await setStoredAgeGateStartupDebugMode(startupDebugMode);
      console.log('[AgeCheckTest] saved startup debug mode', startupDebugMode);
    });
  };

  const handleResetStartupOverride = async () => {
    await runAction('Resetting startup override', async () => {
      await setStoredAgeGateStartupDebugMode(AgeGateStartupDebugMode.Auto);
      console.log('[AgeCheckTest] reset startup debug mode to AUTO');
      setStartupDebugMode(AgeGateStartupDebugMode.Auto);
    });
  };

  const handleReloadApp = async () => {
    await runAction('Reloading app', async () => {
      console.log('[AgeCheckTest] reloading app so the startup override can take effect');
      window.location.reload();
    });
  };

  const handleResetAdultAcknowledgment = async () => {
    await runAction('Resetting adult acknowledgment', async () => {
      await clearStoredIosAdultAcknowledgment(appName);
      console.log('[AgeCheckTest] cleared stored adult acknowledgment state');
      setStoredAdultAcknowledgment(false);
    });
  };

  const handleAgeSignalsCheck = async () => {
    await runAction('Checking age signals', async () => {
      const ageGates = parseAgeGates(ageGateInput);
      console.log('[AgeCheckTest] requesting age signals', {
        platform,
        ageGates,
        androidDebugScenario: isAndroid ? androidDebugScenario : null,
      });
      const result = await InsightAgeCheck.checkAgeSignals({
        ageGates,
        androidDebugScenario: isAndroid ? androidDebugScenario : undefined,
      });
      console.log('[AgeCheckTest] age signals result', result);
      setAgeSignalsResult(result);
    });
  };

  const handleRequiredRegulatoryFeaturesCheck = async () => {
    await runAction('Checking regulatory features', async () => {
      console.log('[AgeCheckTest] running iOS required regulatory features check');
      const result = await InsightAgeCheck.checkRequiredRegulatoryFeatures();
      console.log('[AgeCheckTest] iOS required regulatory features result', result);
      setRegulatoryFeaturesResult(result);
    });
  };

  const handleSignificantUpdateAcknowledgment = async () => {
    await runAction('Showing update acknowledgment', async () => {
      console.log('[AgeCheckTest] showing significant update acknowledgment', {
        platform,
        description: updateDescription,
      });
      const result = await InsightAgeCheck.showSignificantUpdateAcknowledgment({
        description: updateDescription,
      });
      await setStoredIosAdultAcknowledgment(appName);
      console.log('[AgeCheckTest] significant update acknowledgment result', result);
      setSignificantUpdateAcknowledgmentResult(result);
      setStoredAdultAcknowledgment(true);
    });
  };

  const handleSignificantUpdateApproval = async () => {
    await runAction('Requesting update approval', async () => {
      console.log('[AgeCheckTest] requesting significant update approval', {
        platform,
        description: updateDescription,
      });
      const result = await InsightAgeCheck.requestSignificantUpdateApproval({
        description: updateDescription,
      });
      console.log('[AgeCheckTest] significant update approval result', result);
      setSignificantUpdateResult(result);
    });
  };

  const clearResults = () => {
    setEligibilityResult(null);
    setRegulatoryFeaturesResult(null);
    setAgeSignalsResult(null);
    setSignificantUpdateAcknowledgmentResult(null);
    setSignificantUpdateResult(null);
    setErrorMessage('');
    setActiveAction('');
  };

  const serializedEligibilityResult = useMemo(() => (
    eligibilityResult ? JSON.stringify(eligibilityResult, null, 2) : ''
  ), [eligibilityResult]);

  const serializedAgeSignalsResult = useMemo(() => (
    ageSignalsResult ? JSON.stringify(ageSignalsResult, null, 2) : ''
  ), [ageSignalsResult]);

  const serializedRuntimeEnvironmentResult = useMemo(() => (
    runtimeEnvironmentResult ? JSON.stringify(runtimeEnvironmentResult, null, 2) : ''
  ), [runtimeEnvironmentResult]);

  const serializedRegulatoryFeaturesResult = useMemo(() => (
    regulatoryFeaturesResult ? JSON.stringify(regulatoryFeaturesResult, null, 2) : ''
  ), [regulatoryFeaturesResult]);

  const serializedSignificantUpdateAcknowledgmentResult = useMemo(() => (
    significantUpdateAcknowledgmentResult ? JSON.stringify(significantUpdateAcknowledgmentResult, null, 2) : ''
  ), [significantUpdateAcknowledgmentResult]);

  const serializedSignificantUpdateResult = useMemo(() => (
    significantUpdateResult ? JSON.stringify(significantUpdateResult, null, 2) : ''
  ), [significantUpdateResult]);

  return (
    <IonModal
      id="age-check-test"
      className="modal-fullscreen age-check-test-modal"
      isOpen={isOpen}
      canDismiss={true}
      onDidDismiss={onClose}
    >
      <IonContent className="age-check-test-content">
        {platform !== 'web' ? <div className="ad-buffer"></div> : <></>}
        <div className="age-check-test-shell">
          <div className="age-check-test-header">
            <div>
              <div className="age-check-test-kicker">InsightAgeCheck</div>
              <h1>{title}</h1>
              <p>{intro}</p>
            </div>
            <button className="age-check-test-close" onClick={onClose}>
              Close
            </button>
          </div>

          <div className="age-check-test-section">
            <div className="age-check-test-section-title">Device Readiness</div>
            <div className="age-check-test-note">
              Platform detected: <strong>{platform || 'unknown'}</strong>
            </div>
            <div className="age-check-test-note">
              {isAndroid ? (
                <>
                  On Android, the main production signal is <strong>checkAgeSignals</strong>.
                  A local debug build may fail the live check if it was not installed
                  by Google Play.
                </>
              ) : (
                <>
                  Apple recommends running <strong>eligibility</strong> first. If it
                  returns <strong>false</strong>, your production flow would normally
                  show nothing.
                </>
              )}
            </div>
            <div className="age-check-test-note">
              {isAndroid ? (
                <>
                  For live Android testing, use a Play-enabled device with Google
                  Play services and a Play-installed build. For local emulator work,
                  use the Android debug scenarios below.
                </>
              ) : (
                <>
                  For reliable sandbox testing, use a development-signed build on an
                  iPhone or iPad running iOS 26.2+ with Developer Mode, a signed-in
                  Sandbox Apple Account, and Sandbox Testing &gt; Age Assurance configured
                  for the scenario you want to test. Simulator behavior can be inconsistent.
                </>
              )}
            </div>
            {isAndroid ? (
              <div className="age-check-test-note">
                Android uses Google&apos;s Play Age Signals API. Local emulator or
                side-loaded builds may fail the live check because they are not
                Play-owned, so this screen includes official fake Google test
                scenarios for integration testing.
              </div>
            ) : null}
            {isIos ? (
              <div className="age-check-test-note">
                Apple&apos;s sandbox can test age range scenarios, location-based restrictions,
                approval state changes, consent revocation, and significant changes on a real device.
              </div>
            ) : null}
            {isIos ? (
              <div className="age-check-test-note">
                This build includes the full iOS 26.4 Apple age assurance surface:
                <strong> requiredRegulatoryFeatures</strong>, the adult significant update
                acknowledgment sheet, and the existing parent or guardian approval flow.
              </div>
            ) : null}
            {isIos ? (
              <div className="age-check-test-note">
                On iOS 26.4+, Apple&apos;s newer top-level compliance signal is
                <strong> requiredRegulatoryFeatures</strong>. The adult path uses
                <strong> showSignificantUpdateAcknowledgment</strong>, while supervised-child
                testing still uses the separate parent or guardian approval prompt.
              </div>
            ) : null}
            {isIos ? (
              <div className="age-check-test-note">
                Stored adult acknowledgment for the current significant update:
                {' '}
                <strong>{storedAdultAcknowledgment ? 'yes' : 'no'}</strong>
              </div>
            ) : null}
            {!isSupportedPlatform ? (
              <div className="age-check-test-warning">
                {isAndroid
                  ? 'This test screen can run live Google Play checks or Android debug scenarios.'
                  : 'This test screen only works on iOS builds with the Apple age assurance entitlement enabled.'}
              </div>
            ) : null}
            {runtimeEnvironmentResult?.shouldAutoBypass ? (
              <div className="age-check-test-warning">
                Automatic startup bypass is currently active for this runtime:
                {' '}
                {runtimeEnvironmentResult.autoBypassReason}
              </div>
            ) : null}
            {serializedRuntimeEnvironmentResult ? (
              <pre className="age-check-test-result">{serializedRuntimeEnvironmentResult}</pre>
            ) : null}
          </div>

          {supportsStartupOverride ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Startup Override</div>
              <div className="age-check-test-note">
                This only applies to debug builds. Release, TestFlight, and Play-distributed
                builds ignore these overrides and always use the live production path.
              </div>
              <div className="age-check-test-note">
                Save a mode here, then reload the app to force the startup gate into a local
                allow or block path without depending on live Apple or Google responses.
              </div>
              <label className="age-check-test-field">
                <span>Startup behavior</span>
                <select
                  value={startupDebugMode}
                  onChange={event => setStartupDebugMode(event.target.value as AgeGateStartupDebugMode)}
                >
                  {ageGateStartupDebugOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="age-check-test-note">
                {ageGateStartupDebugOptions.find(option => option.value === startupDebugMode)?.description}
              </div>
              <div className="age-check-test-button-row">
                <IonButton disabled={isBusy} onClick={handleSaveStartupOverride}>
                  Save Override
                </IonButton>
                <IonButton fill="outline" disabled={isBusy} onClick={handleResetStartupOverride}>
                  Reset to Auto
                </IonButton>
                <IonButton fill="clear" disabled={isBusy} onClick={handleReloadApp}>
                  Reload App
                </IonButton>
              </div>
            </div>
          ) : null}

          {!supportsStartupOverride && runtimeEnvironmentResult?.isDebugBuild === false ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Startup Override</div>
              <div className="age-check-test-note">
                Startup overrides are only honored in debug builds, so they are currently unavailable.
              </div>
            </div>
          ) : null}

          {canCheckEligibility ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Eligibility</div>
              <IonButton
                disabled={!isSupportedPlatform || isBusy}
                onClick={handleEligibilityCheck}
              >
                Run Eligibility Check
              </IonButton>
              {serializedEligibilityResult ? (
                <pre className="age-check-test-result">{serializedEligibilityResult}</pre>
              ) : null}
            </div>
          ) : null}

          {canCheckRequiredRegulatoryFeatures ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Required Regulatory Features</div>
              <div className="age-check-test-note">
                On iOS 26.4+, this is Apple&apos;s more precise signal for whether declared age range,
                adult notification, or parental consent requirements apply on this device right now.
              </div>
              <IonButton
                disabled={!isSupportedPlatform || isBusy}
                onClick={handleRequiredRegulatoryFeaturesCheck}
              >
                Check Regulatory Features
              </IonButton>
              {serializedRegulatoryFeaturesResult ? (
                <pre className="age-check-test-result">{serializedRegulatoryFeaturesResult}</pre>
              ) : null}
            </div>
          ) : null}

          <div className="age-check-test-section">
            <div className="age-check-test-section-title">Age Signals</div>
            <label className="age-check-test-field">
              <span>Age gates</span>
              <input
                value={ageGateInput}
                onChange={event => setAgeGateInput(event.target.value)}
                placeholder="13, 15, 18"
              />
            </label>
            <div className="age-check-test-note">
              Apple requires two or three increasing age gates. Google Play ignores
              these thresholds but the shared plugin keeps the same method signature.
            </div>
            {isAndroid ? (
              <label className="age-check-test-field">
                <span>Android test mode</span>
                <select
                  value={androidDebugScenario}
                  onChange={event => setAndroidDebugScenario(event.target.value as AndroidDebugScenario)}
                >
                  <option value={AndroidDebugScenario.Live}>Live Google Play API</option>
                  <option value={AndroidDebugScenario.Verified}>Fake verified user</option>
                  <option value={AndroidDebugScenario.Declared}>Fake declared user</option>
                  <option value={AndroidDebugScenario.Supervised}>Fake supervised user</option>
                  <option value={AndroidDebugScenario.SupervisedApprovalPending}>Fake approval pending</option>
                  <option value={AndroidDebugScenario.SupervisedApprovalDenied}>Fake approval denied</option>
                  <option value={AndroidDebugScenario.Unknown}>Fake unknown status</option>
                  <option value={AndroidDebugScenario.NoSignal}>Fake no signal</option>
                  <option value={AndroidDebugScenario.NetworkError}>Fake network error</option>
                </select>
              </label>
            ) : null}
            <IonButton
              disabled={(platform === 'web') || isBusy}
              onClick={handleAgeSignalsCheck}
            >
              Request Age Signals
            </IonButton>
            {serializedAgeSignalsResult ? (
              <pre className="age-check-test-result">{serializedAgeSignalsResult}</pre>
            ) : null}
          </div>

          {canShowSignificantUpdateAcknowledgment ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Adult Significant Update Acknowledgment</div>
              <div className="age-check-test-note">
                Use this to test Apple&apos;s iOS 26.4 system sheet for adults when a significant
                update requires notification before continuing.
              </div>
              <label className="age-check-test-field">
                <span>Update description</span>
                <textarea
                  value={updateDescription}
                  onChange={event => setUpdateDescription(event.target.value)}
                  placeholder="Describe the significant update"
                />
              </label>
              <IonButton
                disabled={!isSupportedPlatform || isBusy}
                onClick={handleSignificantUpdateAcknowledgment}
              >
                Show Adult Acknowledgment
              </IonButton>
              <IonButton
                fill="outline"
                disabled={!isSupportedPlatform || isBusy}
                onClick={handleResetAdultAcknowledgment}
              >
                Reset Stored Acknowledgment
              </IonButton>
              {serializedSignificantUpdateAcknowledgmentResult ? (
                <pre className="age-check-test-result">{serializedSignificantUpdateAcknowledgmentResult}</pre>
              ) : null}
            </div>
          ) : null}

          {canRequestSignificantUpdateApproval ? (
            <div className="age-check-test-section">
              <div className="age-check-test-section-title">Parent or Guardian Approval</div>
              <div className="age-check-test-note">
                Use this to test Apple&apos;s parent or guardian approval prompt for a significant update.
                On a supervised sandbox setup, Apple should return approval or denial for the current scenario.
              </div>
              <label className="age-check-test-field">
                <span>Update description</span>
                <textarea
                  value={updateDescription}
                  onChange={event => setUpdateDescription(event.target.value)}
                  placeholder="Describe the significant update"
                />
              </label>
              <IonButton
                disabled={!isSupportedPlatform || isBusy}
                onClick={handleSignificantUpdateApproval}
              >
                Request Update Approval
              </IonButton>
              {serializedSignificantUpdateResult ? (
                <pre className="age-check-test-result">{serializedSignificantUpdateResult}</pre>
              ) : null}
            </div>
          ) : null}

          {errorMessage ? (
            <div className="age-check-test-error">{errorMessage}</div>
          ) : null}

          {isBusy ? (
            <div className="age-check-test-loading">
              <IonSpinner name="crescent" />
              <span>{activeAction}...</span>
            </div>
          ) : null}

          <div className="age-check-test-footer">
            <IonButton fill="outline" disabled={isBusy} onClick={clearResults}>
              Clear Results
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
}

export default AgeCheckTestModal;
